import type { PaymentMethod } from '@/types/checkout'

/** The methods the checkout knows how to collect, in the order they are offered. */
export const CHECKOUT_METHODS: PaymentMethod[] = ['PIX', 'CREDIT_CARD', 'BOLETO']

/**
 * `allowedPaymentMethods` as an array, whatever shape the API sent.
 *
 * The column is `Json?`, and the API hands it back in two shapes: a real array
 * (products created through the current form) or a string holding JSON
 * (`"[\"CREDIT_CARD\",\"PIX\"]"`, the older ones) — see BUGS.md FIN-27.
 * `.includes` on the string happened to work; `.map` on it threw. Reading both
 * here keeps every consumer on one shape. Unknown methods are dropped, since
 * the checkout cannot collect them.
 *
 * No list at all (null, absent, unreadable) means the product does not
 * restrict methods, which is how the API's `checkPaymentOptions` reads it:
 * every checkout method is offered (FIN-46). Products created through the API
 * without the field used to open with "no payment method enabled" here while
 * the API would have taken any method. An explicit empty list stays empty.
 */
export const parseAllowedPaymentMethods = (value: unknown): PaymentMethod[] => {
  let list: unknown = value
  if (typeof value === 'string') {
    try {
      list = JSON.parse(value)
    } catch {
      list = null
    }
  }
  if (!Array.isArray(list)) return [...CHECKOUT_METHODS]
  return CHECKOUT_METHODS.filter((method) => list.includes(method))
}

/**
 * The method selected when a product is opened.
 *
 * PIX first: it needs nothing but the buyer's data and settles in seconds,
 * so it is the shortest path to paying when the product allows it.
 */
export const defaultPaymentMethod = (allowed: PaymentMethod[]): PaymentMethod | null =>
  CHECKOUT_METHODS.find((method) => allowed.includes(method)) ?? null
